/**
 * Collection Offers API Client
 *
 * Handles API calls for collection-wide buy offers using escrow mechanism.
 */

import { apiClient, ApiResponse } from './client';
import type { XamanPayloadResponse, SigningPayload } from '../types/signing';
import { toSigningPayload } from '../lib/wallet';

export type ExpirationOption = '1_day' | '3_days' | '7_days' | '30_days' | 'no_expiration';

// Trait filter type: { "traitType": ["value1", "value2"], ... }
// OR within trait type, AND between trait types
export type TraitFilters = Record<string, string[]>;

export interface ExpirationOptionData {
  value: ExpirationOption;
  label: string;
  seconds: number | null;
}

export interface CollectionOffer {
  id: string;
  buyerAddress: string;
  collectionId?: string;
  collectionName?: string;
  collectionImageUrl?: string;
  displayAmountXrp: number;
  escrowAmountXrp?: number;
  sellerReceivesXrp: number;
  expiresAt: string | null;
  status?: string;
  createdAt: string;
  filledNftTokenId?: string;
  filledByAddress?: string;
  filledAt?: string;
  traitFilters?: TraitFilters | null;
}

export interface CollectionOfferStats {
  activeOfferCount: number;
  bestOfferXrp: number | null;
}

export interface CreateOfferAmounts {
  displayAmountXrp: number;
  escrowAmountXrp: number;
  sellerReceivesXrp: number;
  buyerFeeXrp: number;
  sellerFeeXrp: number;
}

// websocketUrl stays required: collection-offer modals connect to it unconditionally.
export type XamanPayload = XamanPayloadResponse & { websocketUrl: string };

export interface CreateOfferResponse {
  offerId: string;
  /** @deprecated Xaman-only refs. Use `signing` (normalized across providers). */
  payload?: XamanPayload;
  /** Normalized signing payload — Xaman or joey-wc. */
  signing: SigningPayload;
  amounts: CreateOfferAmounts;
}

export interface AcceptOfferResponse {
  /** @deprecated Xaman-only refs. Use `signing` (normalized across providers). */
  payload?: XamanPayload;
  /** Normalized signing payload — Xaman or joey-wc. */
  signing: SigningPayload;
  sellerReceivesXrp: number;
}

export interface CollectionOffersListResponse {
  offers: CollectionOffer[];
  total: number;
  stats: CollectionOfferStats;
}

export interface UserOffersResponse {
  offers: CollectionOffer[];
  total: number;
}

/**
 * Get expiration options for collection offers
 */
export async function getExpirationOptions(): Promise<{
  options: ExpirationOptionData[];
  defaultOption: ExpirationOption;
}> {
  const response = await apiClient.get<ApiResponse<{
    options: ExpirationOptionData[];
    defaultOption: ExpirationOption;
  }>>('/collection-offers/expiration-options');

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to get expiration options');
  }

  return response.data;
}

/**
 * Create a new collection offer
 */
export async function createCollectionOffer(params: {
  collectionId: string;
  offerAmountXrp: number;
  expiration: ExpirationOption;
  userAddress: string;
  traitFilters?: TraitFilters | null;
}): Promise<CreateOfferResponse> {
  const response = await apiClient.post<ApiResponse<CreateOfferResponse>>(
    '/collection-offers/create',
    params
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to create collection offer');
  }

  const data = response.data as any;
  return { ...response.data, signing: toSigningPayload(data.signingIntent ?? data.payload) };
}

/**
 * Activate an offer after escrow is confirmed
 */
export async function activateOffer(params: {
  offerId: string;
  escrowLedgerIndex?: number;
  escrowCreateTransactionHash?: string;
}): Promise<void> {
  const response = await apiClient.post<ApiResponse<void>>(
    '/collection-offers/activate',
    params
  );

  if (!response.success) {
    throw new Error(response.error || 'Failed to activate offer');
  }
}

/**
 * Get active offers for a collection
 */
export async function getCollectionOffers(
  collectionId: string,
  options: { limit?: number; offset?: number; orderBy?: string } = {}
): Promise<CollectionOffersListResponse> {
  const params = new URLSearchParams();
  if (options.limit) params.set('limit', options.limit.toString());
  if (options.offset) params.set('offset', options.offset.toString());
  if (options.orderBy) params.set('orderBy', options.orderBy);

  const response = await apiClient.get<ApiResponse<CollectionOffersListResponse>>(
    `/collection-offers/collection/${collectionId}?${params}`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to get collection offers');
  }

  return response.data;
}

/**
 * Get user's active collection offers
 */
export async function getUserCollectionOffers(
  address: string,
  options: { limit?: number; offset?: number } = {}
): Promise<UserOffersResponse> {
  const params = new URLSearchParams();
  if (options.limit) params.set('limit', options.limit.toString());
  if (options.offset) params.set('offset', options.offset.toString());

  const response = await apiClient.get<ApiResponse<UserOffersResponse>>(
    `/collection-offers/user/${address}?${params}`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to get user offers');
  }

  return response.data;
}

/**
 * Get a single offer by ID
 */
export async function getCollectionOffer(offerId: string): Promise<CollectionOffer> {
  const response = await apiClient.get<ApiResponse<CollectionOffer>>(
    `/collection-offers/${offerId}`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to get offer');
  }

  return response.data;
}

/**
 * Accept a collection offer (seller creates sell offer)
 */
export async function acceptCollectionOffer(params: {
  offerId: string;
  nftTokenId: string;
  userAddress: string;
}): Promise<AcceptOfferResponse> {
  const response = await apiClient.post<ApiResponse<AcceptOfferResponse>>(
    '/collection-offers/accept',
    params
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to accept collection offer');
  }

  const data = response.data as any;
  return { ...response.data, signing: toSigningPayload(data.signingIntent ?? data.payload) };
}

/**
 * Execute trade after seller's offer is confirmed
 */
export async function executeCollectionOfferTrade(params: {
  offerId: string;
  sellerOfferTxHash: string; // Transaction hash - backend will look up the offer index
  nftTokenId: string;
  sellerAddress: string;
}): Promise<{ buyerOfferIndex?: string; message: string }> {
  const response = await apiClient.post<ApiResponse<{
    offerId: string;
    buyerOfferIndex?: string;
    message: string;
  }>>('/collection-offers/execute-trade', params);

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to execute trade');
  }

  return response.data;
}

/**
 * Cancel a collection offer
 */
export async function cancelCollectionOffer(params: {
  offerId: string;
  userAddress: string;
}): Promise<SigningPayload> {
  const response = await apiClient.post<ApiResponse<{ payload?: XamanPayload; signingIntent?: unknown }>>(
    '/collection-offers/cancel',
    params
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to cancel collection offer');
  }

  const data = response.data as any;
  return toSigningPayload(data.signingIntent ?? data.payload);
}

/**
 * Mark offer as cancelled after EscrowCancel is confirmed
 */
export async function markOfferCancelled(params: {
  offerId: string;
  transactionHash?: string;
}): Promise<void> {
  const response = await apiClient.post<ApiResponse<void>>(
    '/collection-offers/mark-cancelled',
    params
  );

  if (!response.success) {
    throw new Error(response.error || 'Failed to mark offer as cancelled');
  }
}

/**
 * Calculate fee breakdown for a given offer amount
 */
export function calculateOfferFees(offerAmountXrp: number): CreateOfferAmounts {
  const buyerFeeXrp = offerAmountXrp * 0.01;
  const sellerFeeXrp = offerAmountXrp * 0.01;

  return {
    displayAmountXrp: offerAmountXrp,
    escrowAmountXrp: offerAmountXrp + buyerFeeXrp,
    sellerReceivesXrp: offerAmountXrp - sellerFeeXrp,
    buyerFeeXrp,
    sellerFeeXrp,
  };
}

/**
 * Get the best matching collection offer for a specific NFT
 * Returns the highest-priced offer that either:
 * 1. Has no trait filters, or
 * 2. The NFT matches the trait filters
 */
export async function getBestMatchingOfferForNFT(
  nftTokenId: string,
  excludeBuyer?: string
): Promise<CollectionOffer | null> {
  const params = new URLSearchParams();
  if (excludeBuyer) params.set('excludeBuyer', excludeBuyer);

  const response = await apiClient.get<ApiResponse<CollectionOffer | null>>(
    `/collection-offers/nft/${nftTokenId}/best-match?${params}`
  );

  if (!response.success) {
    throw new Error(response.error || 'Failed to get best matching offer');
  }

  return response.data ?? null;
}
